import React from "react";
import cn from "classnames";
import { Icons } from "../shared";

export type PaginationOptions = {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
};

export type PaginationProps = {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    onPageChange: (page: number) => void;
    onPageSizeChange?: (pageSize: number) => void;
    pageSizeOptions?: number[];
    className?: string;
};

export const Pagination: React.FC<PaginationProps> = ({ currentPage, pageSize, totalCount, onPageChange, onPageSizeChange, pageSizeOptions = [10, 25, 50, 100], className }) => {
    const totalPages = Math.ceil(totalCount / pageSize);

    const getPageNumbers = () => {
        const pages = [];
        if (currentPage > 3) {
            pages.push(1);
            if (currentPage > 4) {
                pages.push("...");
            }
        }

        for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages, currentPage + 1); i++) {
            pages.push(i);
        }

        if (currentPage < totalPages - 2) {
            if (currentPage < totalPages - 3) {
                pages.push("...");
            }
            pages.push(totalPages);
        }

        return pages;
    };

    const pageNumbers = getPageNumbers();

    return (
        <div className={cn("flex flex-wrap items-center justify-between gap-2 py-3", className)}>
            <div className="flex items-center">
                <span className="text-sm text-gray-700">
                    Showing <span className="font-medium">{Math.min((currentPage - 1) * pageSize + 1, totalCount)}</span> - <span className="font-medium">{Math.min(currentPage * pageSize, totalCount)}</span> of <span className="font-medium">{totalCount}</span> results
                </span>
            </div>

            <div className="flex items-center space-x-2">
                {onPageSizeChange && (
                    <div className="flex items-center mr-4">
                        <span className="mr-2 text-sm">Items per page:</span>
                        <select className="border rounded px-2 py-1 text-sm" value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))}>
                            {pageSizeOptions.map((size) => (
                                <option key={size} value={size}>
                                    {size}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} className={cn("p-1 rounded flex items-center justify-center", currentPage === 1 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-100")}>
                    <Icons.ChevronLeft className="h-5 w-5" />
                </button>

                {pageNumbers.map((page, index) => (
                    <button
                        key={`page-${index}`}
                        onClick={() => (typeof page === "number" ? onPageChange(page) : null)}
                        disabled={typeof page !== "number"}
                        className={cn("px-3 py-1 rounded text-sm font-medium", {
                            "bg-primary-500 shadow-md transform scale-110": typeof page === "number" && page === currentPage,
                            "hover:bg-gray-100": typeof page === "number" && page !== currentPage,
                            "opacity-50 cursor-default": typeof page !== "number",
                        })}
                    >
                        {page}
                    </button>
                ))}

                <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0} className={cn("p-1 rounded flex items-center justify-center", currentPage === totalPages || totalPages === 0 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-100")}>
                    <Icons.ChevronRight className="h-5 w-5" />
                </button>
            </div>
        </div>
    );
};
